import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import StudentModals from "../studentModals";
import StudentSidebar from "./studentSidebar";
import StudentBreadcrumb from "./studentBreadcrumb";
import { useGetStudentByIdQuery } from "../api/studentApi";
import { useGetAttendanceByStudentQuery } from "../../../hrm/attendance/api/attendanceApi";
import { StudentAttendanceDetailsResponse } from "../../../hrm/attendance/types/attendanceTypes";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

// Add interface for calendar day data
interface CalendarDay {
  date: number | string;
  fullDate: string;
  status: string;
  statusClass: string;
}

const StudentAttendanceDetail = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const [selectedMonth, setSelectedMonth] = useState<string>(
    dayjs().format("YYYY-MM")
  ); // Default to current month
  const [highlightedDate, setHighlightedDate] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [viewType, setViewType] = useState<"year" | "month">("month");

  const {
    data: studentData,
    isLoading: studentLoading,
    error,
  } = useGetStudentByIdQuery({ id: id || "" });

  // Helper function to get status class
  const getStatusClass = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "present":
        return "bg-success";
      case "ncns":
        return "bg-warning";
      case "leave":
        return "bg-dark";
      case "absent":
        return "bg-danger";
      case "dropout":
        return "bg-secondary";
      default:
        return "bg-info";
    }
  };

  const { data: attendanceResponse, isLoading: attendanceLoading } =
    useGetAttendanceByStudentQuery(
      {
        id: Number(id),
        month: selectedMonth,
      },
      {
        skip: !id,
      }
    );

  // Process attendance data for calendar view
  const processedAttendanceData = React.useMemo(() => {
    if (!attendanceResponse?.data?.attendance_records) return [];

    const records = attendanceResponse.data.attendance_records;
    const currentMonth = selectedMonth ? dayjs(selectedMonth) : dayjs();
    const daysInMonth = currentMonth.daysInMonth();

    // Create calendar grid
    const calendarData: CalendarDay[] = Array.from(
      { length: daysInMonth },
      (_, index) => {
        const day = index + 1;
        const date = currentMonth.date(day).format("YYYY-MM-DD");

        // Find attendance record for this date
        const record = records.find((r) => r.date === date);

        return {
          date: day,
          fullDate: date,
          status: record ? record.attendance_status : "-",
          statusClass: getStatusClass(record?.attendance_status),
        };
      }
    );

    return calendarData;
  }, [attendanceResponse, selectedMonth]);

  // Define calendar columns
  const calendarColumns = [
    { title: "Sun", dataIndex: "sun" },
    { title: "Mon", dataIndex: "mon" },
    { title: "Tue", dataIndex: "tue" },
    { title: "Wed", dataIndex: "wed" },
    { title: "Thu", dataIndex: "thu" },
    { title: "Fri", dataIndex: "fri" },
    { title: "Sat", dataIndex: "sat" },
  ];

  // Organize data into weeks
  const calendarWeeks = React.useMemo(() => {
    if (!processedAttendanceData.length) return [];

    const firstDay = dayjs(selectedMonth).startOf("month").day();
    const weeks: (CalendarDay | null)[][] = [];
    let currentWeek: (CalendarDay | null)[] = Array(7).fill(null);

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      currentWeek[i] = {
        date: "",
        fullDate: "",
        status: "",
        statusClass: "",
      };
    }

    // Fill in the days of the month
    processedAttendanceData.forEach((day: CalendarDay, index: number) => {
      const weekDay = (firstDay + index) % 7;
      if (weekDay === 0 && index !== 0) {
        weeks.push(currentWeek);
        currentWeek = Array(7).fill(null);
      }
      currentWeek[weekDay] = day;
    });

    // Add the last week if it's not empty
    if (currentWeek.some((day) => day !== null)) {
      weeks.push(currentWeek);
    }

    return weeks;
  }, [processedAttendanceData, selectedMonth]);

  // Handle filter changes
  const handleFilterChange = (filter: string) => {
    switch (filter) {
      case "This Year":
        setViewType("year");
        setSelectedMonth("");
        setSelectedYear(dayjs().year());
        break;
      case "This Month":
        setViewType("month");
        setSelectedMonth(dayjs().format("YYYY-MM"));
        break;
    }
  };

  const handleMonthChange = (newMonth: string) => {
    setSelectedMonth(newMonth);
    setHighlightedDate("");
  };

  const handleDateSelect = (fullDate: string) => {
    setHighlightedDate(fullDate);
  };

  const currentMonthYear = dayjs(selectedMonth).format("MMMM YYYY");

  // Add legend items for attendance status
  const legendItems = [
    { status: "Present", class: "bg-success" },
    { status: "Leave", class: "bg-dark" },
    { status: "Absent", class: "bg-danger" },
    { status: "NCNS", class: "bg-warning" },
    { status: "Dropout", class: "bg-secondary" },
  ];

  const navigateMonth = (direction: "prev" | "next") => {
    // Only allow going to next month if it's not in the future
    if (direction === "next") {
      const nextMonth = dayjs(selectedMonth).add(1, "month");
      const currentDate = dayjs();

      // Check if the next month is in the future
      if (nextMonth.isAfter(currentDate, "month")) {
        return; // Don't navigate to future months
      }
    }

    const newMonth = dayjs(selectedMonth)
      .add(direction === "next" ? 1 : -1, "month")
      .format("YYYY-MM");
    handleMonthChange(newMonth);
  };

  // Array of month names
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleYearChange = (increment: number) => {
    const currentYear = dayjs().year();
    const newYear = selectedYear + increment;

    // Only update if not trying to go to a future year
    if (increment < 0 || newYear <= currentYear) {
      setSelectedYear(newYear);
    }
  };

  // Add this function to check if the next month/year would be in the future
  const isNextDisabled = () => {
    if (viewType === "year") {
      return selectedYear >= dayjs().year();
    } else {
      const nextMonth = dayjs(selectedMonth).add(1, "month");
      return nextMonth.isAfter(dayjs(), "month");
    }
  };

  // First, add a function to check if a date is in the future
  const isFutureDate = (date: string) => {
    return dayjs(date).isAfter(dayjs(), "day");
  };

  // Render different calendar views
  const renderYearView = () => (
    <div className="table-responsive">
      <table className="table table-bordered text-center mb-0">
        <thead>
          <tr>
            <th style={{ backgroundColor: "#f8f9fa" }}>Date</th>
            {months.map((month) => (
              <th key={month} style={{ backgroundColor: "#f8f9fa" }}>
                {month.slice(0, 3)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
            <tr key={day}>
              <td className="fw-semibold">{day}</td>
              {months.map((month, monthIndex) => {
                const date = dayjs()
                  .year(selectedYear)
                  .month(monthIndex)
                  .date(day);
                const record =
                  attendanceResponse?.data?.attendance_records?.find(
                    (r) => r.date === date.format("YYYY-MM-DD")
                  );

                return (
                  <td
                    key={monthIndex}
                    className="position-relative"
                    style={{
                      height: "40px",
                      opacity: date.isAfter(dayjs(), "day") ? 0.5 : 1, // Reduce opacity for future dates
                      backgroundColor: date.isAfter(dayjs(), "day")
                        ? "#f8f9fa"
                        : "transparent", // Light background for future dates
                    }}
                  >
                    {date.isValid() && record?.attendance_status && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-${monthIndex}-${day}`}>
                            {`${date.format("MMMM DD, YYYY")} - ${
                              record.attendance_status
                            }`}
                          </Tooltip>
                        }
                      >
                        <div className="d-flex justify-content-center">
                          <span
                            className={getStatusClass(record.attendance_status)}
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              display: "inline-block",
                            }}
                          />
                        </div>
                      </OverlayTrigger>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (studentLoading || attendanceLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  <div className="card">
                    <div className="card-body p-0 py-3">
                      <div className="text-center">
                        Loading attendance data...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  <div className="card">
                    <div className="card-body p-0 py-3">
                      <div className="text-center text-danger">
                        Error loading attendance data
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats: StudentAttendanceDetailsResponse = attendanceResponse?.data || {
    totalRecords: 0,
    presentTotal: 0,
    absentTotal: 0,
    leaveTotal: 0,
    percentagePresent: 0,
    percentageAbsent: 0,
    percentageLeave: 0,
    attendance_records: [],
  };

  const getHeaderText = () => {
    if (viewType === "year") {
      return `Year ${selectedYear}`;
    }
    return currentMonthYear;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-1">
                      <h4 className="mb-3">Attendance</h4>
                      <div className="d-flex align-items-center flex-wrap">
                        <div className="dropdown me-3">
                          <button
                            className="btn btn-outline-light bg-white dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-calendar-due me-2" />
                            {getHeaderText()}
                          </button>
                          <ul className="dropdown-menu p-3">
                            <li>
                              <button
                                className="dropdown-item rounded-1"
                                onClick={() => handleFilterChange("This Year")}
                              >
                                This Year
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item rounded-1"
                                onClick={() => handleFilterChange("This Month")}
                              >
                                This Month
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-6 col-xxl-3 d-flex">
                          <div className="d-flex flex-column rounded border p-3 mb-3 flex-fill">
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg bg-success-transparent rounded me-2 flex-shrink-0 text-success">
                                <i className="ti ti-user-check fs-24" />
                              </span>
                              <div className="ms-2">
                                <p
                                  className="mb-1"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  Present ({stats.percentagePresent}%)
                                </p>
                                <h5>{stats.presentTotal}</h5>
                              </div>
                            </div>
                            <div
                              className="progress mt-2 bg-light"
                              style={{ height: "6px", borderRadius: "3px" }}
                            >
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{
                                  width: `${stats.percentagePresent}%`,
                                  transition: "width 1s ease-in-out",
                                  borderRadius: "3px",
                                  boxShadow: "1px 1px 4px rgba(0,0,0,0.1)",
                                }}
                                aria-valuenow={stats.percentagePresent}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 col-xxl-3 d-flex">
                          <div className="d-flex flex-column rounded border p-3 mb-3 flex-fill">
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg bg-danger-transparent rounded me-2 flex-shrink-0 text-danger">
                                <i className="ti ti-user-x fs-24" />
                              </span>
                              <div className="ms-2">
                                <p
                                  className="mb-1"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  Absent ({stats.percentageAbsent}%)
                                </p>
                                <h5>{stats.absentTotal}</h5>
                              </div>
                            </div>
                            <div
                              className="progress mt-2 bg-light"
                              style={{ height: "6px", borderRadius: "3px" }}
                            >
                              <div
                                className="progress-bar bg-danger"
                                role="progressbar"
                                style={{
                                  width: `${stats.percentageAbsent}%`,
                                  transition: "width 1s ease-in-out",
                                  borderRadius: "3px",
                                  boxShadow: "1px 1px 4px rgba(0,0,0,0.1)",
                                }}
                                aria-valuenow={stats.percentageAbsent}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 col-xxl-3 d-flex">
                          <div className="d-flex flex-column rounded border p-3 mb-3 flex-fill">
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg bg-dark-transparent rounded me-2 flex-shrink-0 text-dark">
                                <i className="ti ti-clock-x fs-24" />
                              </span>
                              <div className="ms-2">
                                <p
                                  className="mb-1"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  Leave ({stats.percentageLeave}%)
                                </p>
                                <h5>{stats.leaveTotal}</h5>
                              </div>
                            </div>
                            <div
                              className="progress mt-2 bg-light"
                              style={{ height: "6px", borderRadius: "3px" }}
                            >
                              <div
                                className="progress-bar bg-dark"
                                role="progressbar"
                                style={{
                                  width: `${stats.percentageLeave}%`,
                                  transition: "width 1s ease-in-out",
                                  borderRadius: "3px",
                                  boxShadow: "1px 1px 4px rgba(0,0,0,0.1)",
                                }}
                                aria-valuenow={stats.percentageLeave}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 col-xxl-3 d-flex">
                          <div className="d-flex flex-column rounded border p-3 mb-3 flex-fill">
                            <div className="d-flex align-items-center">
                              <span className="avatar avatar-lg bg-info-transparent rounded me-2 flex-shrink-0 text-info">
                                <i className="ti ti-calendar-stats fs-24" />
                              </span>
                              <div className="ms-2">
                                <p
                                  className="mb-1"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  Total Records
                                </p>
                                <h5>{stats.totalRecords}</h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-body p-0 py-3">
                      <div className="px-3">
                        {/* Calendar Header */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div className="d-flex align-items-center">
                            <h5 className="card-title mb-0 me-3">
                              Attendance Calendar
                            </h5>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-light btn-sm"
                                onClick={() =>
                                  viewType === "year"
                                    ? handleYearChange(-1)
                                    : navigateMonth("prev")
                                }
                                title={
                                  viewType === "year"
                                    ? "Previous Year"
                                    : "Previous Month"
                                }
                              >
                                <i className="ti ti-chevron-left" />
                              </button>

                              <div className="badge bg-primary">
                                {viewType === "year"
                                  ? selectedYear
                                  : currentMonthYear}
                              </div>

                              <button
                                className="btn btn-light btn-sm"
                                onClick={() =>
                                  viewType === "year"
                                    ? handleYearChange(1)
                                    : navigateMonth("next")
                                }
                                disabled={isNextDisabled()}
                                title={
                                  viewType === "year"
                                    ? "Next Year"
                                    : "Next Month"
                                }
                              >
                                <i className="ti ti-chevron-right" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Attendance Legend */}
                        <div className="d-flex gap-3 mb-4 flex-wrap">
                          {legendItems.map((item) => (
                            <div
                              key={item.status}
                              className="d-flex align-items-center"
                            >
                              <span
                                className={`${item.class} me-2`}
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  display: "inline-block",
                                  borderRadius: "2px",
                                }}
                              />
                              <small className="text-muted">
                                {item.status}
                              </small>
                            </div>
                          ))}
                        </div>

                        {/* Calendar Views */}
                        {viewType === "year" && renderYearView()}
                        {viewType === "month" && (
                          <div className="table-responsive">
                            <table className="table table-bordered text-center mb-0">
                              <thead>
                                <tr>
                                  {calendarColumns.map((col) => (
                                    <th
                                      key={col.title}
                                      className="text-center py-3"
                                      style={{ backgroundColor: "#f8f9fa" }}
                                    >
                                      <small className="text-muted fw-semibold">
                                        {col.title}
                                      </small>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {calendarWeeks.map((week, weekIndex) => (
                                  <tr key={weekIndex}>
                                    {week.map(
                                      (
                                        day: CalendarDay | null,
                                        dayIndex: number
                                      ) => (
                                        <td
                                          key={dayIndex}
                                          className={`position-relative ${
                                            day?.fullDate === highlightedDate
                                              ? "bg-light"
                                              : day?.fullDate &&
                                                isFutureDate(day.fullDate)
                                              ? "bg-light-300" // Light background for future dates
                                              : ""
                                          }`}
                                          onClick={() =>
                                            day?.fullDate &&
                                            !isFutureDate(day.fullDate)
                                              ? handleDateSelect(day.fullDate)
                                              : null
                                          }
                                          style={{
                                            height: "80px",
                                            width: "80px",
                                            cursor:
                                              day?.date &&
                                              !isFutureDate(day?.fullDate || "")
                                                ? "pointer"
                                                : "default",
                                            border:
                                              day?.fullDate === highlightedDate
                                                ? "2px solid #0d6efd"
                                                : "1px solid #dee2e6",
                                            transition: "all 0.2s ease",
                                            verticalAlign: "middle",
                                            opacity:
                                              day?.fullDate &&
                                              isFutureDate(day.fullDate)
                                                ? 0.5
                                                : 1, // Reduce opacity for future dates
                                          }}
                                          onMouseEnter={(e) => {
                                            if (
                                              day?.date &&
                                              !isFutureDate(day?.fullDate || "")
                                            ) {
                                              e.currentTarget.style.backgroundColor =
                                                "rgba(13, 110, 253, 0.03)";
                                              e.currentTarget.style.boxShadow =
                                                "0 0 0 1px #0d6efd";
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (
                                              day?.date &&
                                              day.fullDate !==
                                                highlightedDate &&
                                              !isFutureDate(day?.fullDate || "")
                                            ) {
                                              e.currentTarget.style.backgroundColor =
                                                "";
                                              e.currentTarget.style.boxShadow =
                                                "";
                                            }
                                          }}
                                        >
                                          {day?.date && (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip
                                                  id={`tooltip-${day.fullDate}`}
                                                >
                                                  {dayjs(day.fullDate).format(
                                                    "DD-MM-YYYY"
                                                  )}
                                                  {isFutureDate(day.fullDate)
                                                    ? " - Future Date"
                                                    : day.status &&
                                                      day.status !== "-"
                                                    ? ` - ${day.status}`
                                                    : ""}
                                                </Tooltip>
                                              }
                                            >
                                              <div className="d-flex flex-column h-100 align-items-center">
                                                <span
                                                  className={`${
                                                    dayjs(day.fullDate).isSame(
                                                      dayjs(),
                                                      "day"
                                                    )
                                                      ? "badge bg-primary mb-2"
                                                      : isFutureDate(
                                                          day.fullDate
                                                        )
                                                      ? "text-muted mb-2" // Muted text for future dates
                                                      : "mb-2"
                                                  }`}
                                                >
                                                  {day.date}
                                                </span>
                                                {day.status &&
                                                  day.status !== "-" && (
                                                    <span
                                                      className={
                                                        day.statusClass
                                                      }
                                                      style={{
                                                        width: "10px",
                                                        height: "10px",
                                                        borderRadius: "50%",
                                                        display: "inline-block",
                                                        marginTop: "4px",
                                                      }}
                                                    />
                                                  )}
                                              </div>
                                            </OverlayTrigger>
                                          )}
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <StudentModals />
    </>
  );
};

export default StudentAttendanceDetail;
